import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCandidate } from '../hooks/useApi';
import {
  Mail,
  Phone,
  MapPin,
  ArrowLeft,
  ExternalLink,
  Briefcase,
  Calendar,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { StatusBadge } from '../components/StatusBadge';
import { ResumeViewer } from '../components/ResumeViewer';
import { Spinner } from '../components/ui';

export default function CandidateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const candidateId = parseInt(id || '0', 10);

  const { data: candidate, isLoading, error, refetch } = useCandidate(candidateId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="card p-6">
        <div className="text-center">
          <p className="text-red-600">
            Error loading candidate: {error?.message || 'Candidate not found'}
          </p>
          <button onClick={() => navigate('/candidates')} className="btn btn-primary mt-4">
            Back to Candidates
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/candidates')}
          className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Candidate Details</h1>
          <p className="text-sm text-gray-500">
            Added {formatDistanceToNow(new Date(candidate.createdAt), { addSuffix: true })}
          </p>
        </div>
      </div>

      {/* Profile Header */}
      <div className="card p-6">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <span className="text-3xl font-semibold">
              {candidate.firstName.charAt(0)}
              {candidate.lastName.charAt(0)}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {candidate.firstName} {candidate.lastName}
                </h2>
                <p className="text-sm text-gray-500">ID: {candidate.srCandidateId}</p>
              </div>
              <a
                href={`https://www.smartrecruiters.com/app/people/candidates/${candidate.srCandidateId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                View in SmartRecruiters
              </a>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="h-4 w-4 text-gray-400" />
                <a href={`mailto:${candidate.email}`} className="hover:text-primary-600">
                  {candidate.email}
                </a>
              </div>
              {candidate.phone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <a href={`tel:${candidate.phone}`} className="hover:text-primary-600">
                    {candidate.phone}
                  </a>
                </div>
              )}
              {candidate.location && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span>{candidate.location}</span>
                </div>
              )}
            </div>

            {candidate.source && (
              <div className="mt-4">
                <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                  Source: {candidate.source}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Applications */}
      <div className="card overflow-hidden">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Applications</h3>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
              {candidate.applications.length}
            </span>
          </div>
        </div>

        {candidate.applications.length === 0 ? (
          <div className="p-12 text-center">
            <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-gray-500">No applications found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Job
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Applied
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {candidate.applications.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/jobs/${application.jobId}`}
                        className="font-medium text-primary-600 hover:text-primary-700"
                      >
                        Job #{application.jobId}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={application.status} size="sm" />
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {application.srStage || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span title={format(new Date(application.createdAt), 'PPpp')}>
                          {formatDistanceToNow(new Date(application.createdAt), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/applications/${application.id}`}
                        className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resume */}
      <ResumeViewer
        resumeText={candidate.resumeText}
        defaultExpanded={false}
        applicationId={candidate.applications?.[0]?.id}
        candidateId={candidate.id}
        onResumeUpdated={() => refetch()}
      />
    </div>
  );
}
